// frontend/src/content.js
import React, { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import styled from "styled-components";
import coinData from "./coins.json";
import fiatData from "./fiat.json";
import { MdToggleOn, MdToggleOff } from "react-icons/md";

const currency_data = [...coinData, ...fiatData];

const fiatOptions = ["USD", "CAD", "EUR", "AED", "INR", "PKR"];
const PAIRS_PER_PAGE = 18;
const PAIRS_PER_COLUMN = 9;
const PAGE_INTERVAL_MS = 7000;

// Static data moved to module level - computed once at load time
const chunkedPages = [];
for (let i = 0; i < currency_data.length; i += PAIRS_PER_PAGE) {
  chunkedPages.push(currency_data.slice(i, i + PAIRS_PER_PAGE));
}

const allFiatSymbols = [
  "USD", "CAD", "EUR", "AED", "INR", "PKR", "AUD", "AWG", "BHD", "BRL",
  "CHF", "CNH", "COP", "DOP", "GBP", "HKD", "HUF", "ILS", "JOD", "JPY",
  "KWD", "MXN", "NGN", "NOK", "OMR", "PHP", "QAR", "SAR", "SGD", "ZAR",
  "LKR", "IDR", "BDT"
];

const decimalCoinsSet = new Set([
  "DOGE", "XLM", "POL", "TRX", "USD", "USDC", "AED", "PKR", "AUD", "AWG",
  "BHD", "BRL", "CAD", "CHF", "CNH", "COP", "DOP", "EUR", "GBP", "HKD",
  "HUF", "ILS", "INR", "JOD", "JPY", "KWD", "MXN", "NGN", "NOK", "OMR",
  "PHP", "QAR", "SAR", "SGD", "ZAR"
]);

const allFiatSymbolsSet = new Set(allFiatSymbols);

// Static error handler - defined once
const handleImageError = (e) => {
  e.target.src = "/api/images/placeholder.png";
};

const Content = ({ toggleMusic, musicPlaying }) => {
  const [pricesBuy, setPricesBuy] = useState([]);
  const [rates, setRates] = useState([]);
  const [selectedFiat, setSelectedFiat] = useState("USD");
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    document.title = "Coin Nerds | Live Rates";
  }, []);

  // Fetch with AbortController, overlap guard, and Page Visibility API
  useEffect(() => {
    const controller = new AbortController();
    let isFetching = false;
    let interval = null;

    const fetchData = async () => {
      if (isFetching) return;
      isFetching = true;

      try {
        const res = await fetch("/api/liverates", { signal: controller.signal });
        if (!res.ok) throw new Error("Failed to fetch live rates");

        const data = await res.json();

        const priceMap = new Map();
        currency_data.forEach((coin) => {
          const symbol = coin.symbol;
          if (symbol === "USDC") {
            priceMap.set("USDC", data.prices?.["USDC"] ?? null);
          } else if (symbol === "USD") {
            priceMap.set("USD", { bid: 1, ask: 1 });
          } else if (data.prices?.[symbol] != null) {
            priceMap.set(symbol, data.prices[symbol]);
          } else if (data.rates?.[symbol] != null) {
            priceMap.set(symbol, data.rates[symbol]);
          } else {
            priceMap.set(symbol, null);
          }
        });

        const rateList = fiatOptions.map((fiat) =>
          fiat === "USD" ? { bid: 1, ask: 1 } : data.rates?.[fiat] ?? null
        );

        setPricesBuy(Array.from(priceMap.values()));
        setRates(rateList);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetching live rates:", err);
        }
      } finally {
        isFetching = false;
      }
    };

    const startPolling = () => {
      fetchData();
      interval = setInterval(fetchData, 750);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    startPolling();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      controller.abort();
      stopPolling();
    };
  }, []);

  // Page rotation interval
  useEffect(() => {
    const interval = setInterval(() => {
      setPageIndex((prev) =>
        (prev + 1) % Math.ceil(currency_data.length / PAIRS_PER_PAGE)
      );
    }, PAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const fiatIndex = fiatOptions.indexOf(selectedFiat);

  // Memoize priceMap to prevent recreation on every render
  const priceMap = useMemo(() => {
    const map = new Map();
    currency_data.forEach((coin, idx) => {
      map.set(coin.symbol, pricesBuy[idx]);
    });
    return map;
  }, [pricesBuy]);

  const currentCoins = chunkedPages[pageIndex] || [];
  const leftCoins = currentCoins.slice(0, PAIRS_PER_COLUMN);
  const rightCoins = currentCoins.slice(PAIRS_PER_COLUMN, PAIRS_PER_PAGE);

  return (
    <Wrapper>
      <Header>
        <CenteredLogo>
          <img
            src="/api/images/logo.svg"
            alt="Coin Nerds Logo"
            style={{ height: "115px" }}
          />
        </CenteredLogo>
      </Header>

      <SideBySideWrapper>
        <Column>
          {renderCoinRows(leftCoins, priceMap, rates, fiatIndex, selectedFiat)}
        </Column>
        <Column>
          {renderCoinRows(rightCoins, priceMap, rates, fiatIndex, selectedFiat)}
        </Column>
      </SideBySideWrapper>

      <Controls>
        <FormControl variant="outlined" size="small">
          <Select
            value={selectedFiat}
            onChange={(e) => setSelectedFiat(e.target.value)}
            sx={{ color: "black", backgroundColor: "#eee", minWidth: "100px" }}
          >
            {fiatOptions.map((fiat, idx) => (
              <MenuItem value={fiat} key={idx}>
                {fiat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButton onClick={toggleMusic}>
          {musicPlaying ? (
            <MdToggleOn size={30} color="#0f0" />
          ) : (
            <MdToggleOff size={30} color="#f00" />
          )}
          <span style={{ color: "black", marginLeft: "0.5rem" }}>Sound</span>
        </ToggleButton>
      </Controls>
    </Wrapper>
  );
};

export default Content;

const renderCoinRows = (coins, priceMap, rates, fiatIndex, selectedFiat) => {
  const selectedFiatRate = rates[fiatIndex];

  return [
    <HeaderRow key="header">
      <Coin />
      <PriceLabel>BUY</PriceLabel>
      <PriceLabel>SELL</PriceLabel>
    </HeaderRow>,
    ...coins.map((coin) => {
      const baseValue = priceMap.get(coin.symbol);
      const decimals = decimalCoinsSet.has(coin.symbol) ? 4 : 2;

      let buyPrice = "\u2014";
      let sellPrice = "\u2014";

      if (baseValue != null && selectedFiatRate) {
        if (coin.symbol === selectedFiat) {
          buyPrice = "1.0000";
          sellPrice = "1.0000";
        } else if (allFiatSymbolsSet.has(coin.symbol)) {
          const selectedRate = priceMap.get(selectedFiat);
          const targetRate = priceMap.get(coin.symbol);
          if (selectedRate && targetRate) {
            const bidCalc = targetRate?.bid / selectedRate?.ask;
            const askCalc = targetRate?.ask / selectedRate?.bid;
            buyPrice = bidCalc ? bidCalc.toFixed(decimals) : "\u2014";
            sellPrice = askCalc ? askCalc.toFixed(decimals) : "\u2014";
          }
        } else {
          if (coin.symbol === "USDC") {
            buyPrice = ((1 / selectedFiatRate?.ask) * baseValue?.bid).toFixed(decimals);
            sellPrice = ((1 / selectedFiatRate?.bid) * baseValue?.ask).toFixed(decimals);
          } else {
            buyPrice = (baseValue?.bid * selectedFiatRate?.bid).toFixed(decimals);
            sellPrice = (baseValue?.ask * selectedFiatRate?.ask).toFixed(decimals);
          }
        }
      }

      return (
        <DataRow key={coin.symbol}>
          <Coin>
            <img
              src={`/api/images/${coin.image_url}`}
              alt={coin.name}
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
              onError={handleImageError}
            />
            <span>{coin.name}</span>
          </Coin>
          <Price buy>{buyPrice}</Price>
          <Price>{sellPrice}</Price>
        </DataRow>
      );
    }),
  ];
};

// Styled Components
const Wrapper = styled(Box)`
  padding: 2rem;
  color: black;
  position: relative;
  z-index: 1;
  padding-bottom: 5rem;
`;

const Header = styled(Box)`
  text-align: center;
  margin-bottom: 2rem;
`;

const CenteredLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SideBySideWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 3rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 400px;
`;

// Split into HeaderRow and DataRow to avoid dynamic prop recalculation
const rowBase = `
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  gap: 9rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const HeaderRow = styled.div`
  ${rowBase}
  background: transparent;
  border-radius: 0;
  margin-bottom: 0.25rem;
`;

const DataRow = styled.div`
  ${rowBase}
  background: #f9f9f9;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Coin = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 1.6rem;
  flex: 1;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ buy }) => (buy ? "#0f9d58" : "#db4437")};
  min-width: 180px;
  text-align: right;
`;

const PriceLabel = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  min-width: 180px;
  text-align: right;
  color: #555;
  white-space: nowrap;
`;

const Controls = styled(Box)`
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2rem;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  z-index: 1000;
`;

const ToggleButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
